import {
  reverseRecordsDiff,
  squashRecordDiffs,
  type RecordsDiff,
} from "@tldraw/store";
import { atom } from "@tldraw/state";
import { isEqual } from "@tldraw/utils";
import type { TLRecord, TLShapeId, TLStore } from "@tldraw/tlschema";
import { applicableHistoryDiff, rebaseHistoryDiff } from "./history-diff";

// Undo/redo for this user's document edits. Remote changes are never recorded.
// Entries are captured synchronously so they line up with stopping points.

/** A stopping point and the changes recorded after it. */
type Step = {
  mark?: string;
  label?: string;
  open?: boolean;
  held?: boolean;
  checkpoint?: boolean;
  hadChanges?: boolean;
  redoBefore?: Step[];
  diff: RecordsDiff<TLRecord> | null;
  selection: TLShapeId[];
};
type Interceptor = (
  entry: { changes: RecordsDiff<TLRecord> },
  source: "user" | "remote",
) => void;

const isDocument = (store: TLStore, record: TLRecord) =>
  (store.schema.types as unknown as Record<string, { scope: string }>)[
    record.typeName
  ]?.scope === "document";

function documentOnly(store: TLStore, diff: RecordsDiff<TLRecord>) {
  const out: RecordsDiff<TLRecord> = { added: {}, updated: {}, removed: {} };
  let any = false;
  for (const [id, r] of Object.entries(diff.added))
    if (isDocument(store, r))
      (((out.added as Record<string, TLRecord>)[id] = r), (any = true));
  for (const [id, pair] of Object.entries(diff.updated))
    if (isDocument(store, pair[1]) && !isEqual(pair[0], pair[1]))
      (((out.updated as Record<string, [TLRecord, TLRecord]>)[id] = pair),
        (any = true));
  for (const [id, r] of Object.entries(diff.removed))
    if (isDocument(store, r))
      (((out.removed as Record<string, TLRecord>)[id] = r), (any = true));
  return any ? out : null;
}

const managers = new WeakMap<TLStore, HistoryManager>();

/** Keep sync's temporary rollback/replay out of user history. The callback
 * returns only canonical remote changes, or null for a net correction. */
export function reconcileStoreHistory(
  store: TLStore,
  fn: () => RecordsDiff<TLRecord> | null,
  correction = false,
) {
  const manager = managers.get(store);
  if (manager) manager.reconcile(fn, correction);
  else fn();
}

export type HistoryState = {
  undo: string | null;
  redo: string | null;
  message: string;
};

export class HistoryManager {
  private readonly version = atom("history version", 0);
  private message = "";
  private reconciling = 0;
  private disposed = false;
  private inputMark?: string;
  touch() {
    this.version.set(this.version.get() + 1);
  }
  getState(): HistoryState {
    this.version.get();
    const label = (steps: Step[]) =>
      [...steps].reverse().find((s) => s.hadChanges)?.label ?? null;
    return {
      undo: label(this.undos),
      redo: label(this.redos),
      message: this.message,
    };
  }
  private rebase(diff: RecordsDiff<TLRecord>, derived = false) {
    const visited = new Set<Step>();
    const visit = (steps: Step[]) => {
      for (const step of steps) {
        if (visited.has(step)) continue;
        visited.add(step);
        if (step.diff)
          step.diff = documentOnly(
            this.store,
            rebaseHistoryDiff(step.diff, diff, derived),
          );
        if (step.redoBefore) visit(step.redoBefore);
      }
    };
    visit([...this.undos, ...this.redos]);
    this.touch();
  }
  reconcile(fn: () => RecordsDiff<TLRecord> | null, correction = false) {
    let remote: RecordsDiff<TLRecord> | null = null;
    this.reconciling++;
    let visible: RecordsDiff<TLRecord>;
    try {
      visible = this.store.extractingChanges(() => {
        remote = fn();
      });
    } finally {
      this.reconciling--;
    }
    this.rebase(correction ? visible : (remote ?? visible), correction);
  }
  private undos: Step[] = [];
  private redos: Step[] = [];
  private paused = 0;
  private preserveRedo = 0;
  private counter = 0;
  private readonly stop: () => void;

  constructor(
    private readonly store: TLStore,
    private readonly selection: () => TLShapeId[],
    private readonly restoreSelection: (ids: TLShapeId[]) => void,
    private readonly canChange: (
      before: TLRecord | undefined,
      after: TLRecord | undefined,
    ) => boolean = () => true,
  ) {
    // Store.addHistoryInterceptor is MIT @tldraw/store 5.4.2's synchronous change
    // hook; it is not in the published type declarations, so it is typed here.
    this.stop = (
      store as unknown as { addHistoryInterceptor(fn: Interceptor): () => void }
    ).addHistoryInterceptor((entry, source) => {
      if (this.reconciling) return;
      const diff = documentOnly(store, entry.changes);
      if (!diff) return;
      if (source === "remote") {
        this.rebase(diff);
        return;
      }
      if (this.paused) {
        if (!this.replaying) this.rebase(diff, true);
        return;
      }
      let step = this.undos[this.undos.length - 1];
      if (!step?.open) {
        this.mark("Edit contents");
        step = this.undos[this.undos.length - 1];
      }
      step.redoBefore ??= this.redos;
      step.diff = documentOnly(
        store,
        step.diff ? squashRecordDiffs([step.diff, diff]) : diff,
      );
      step.hadChanges = !!step.diff;
      if (!this.preserveRedo)
        this.redos = step.hadChanges ? [] : step.redoBefore;
      this.message = "";
      this.touch();
    });
    managers.set(store, this);
  }

  dispose() {
    this.disposed = true;
    managers.delete(this.store);
    this.stop();
  }

  /** Run without recording ("ignore") or without clearing redo ("record-preserveRedoStack"). */
  run(
    fn: () => void,
    mode: "ignore" | "record" | "record-preserveRedoStack" = "record",
  ) {
    if (mode === "ignore") this.paused++;
    if (mode === "record-preserveRedoStack") this.preserveRedo++;
    try {
      fn();
    } finally {
      if (mode === "ignore") this.paused--;
      if (mode === "record-preserveRedoStack") this.preserveRedo--;
    }
  }

  /** Start a new step; undo returns to this point. */
  mark(name?: string, options: { hold?: boolean; checkpoint?: boolean } = {}) {
    if (
      !options.hold &&
      this.inputMark &&
      this.undos.some((s) => s.mark === this.inputMark && s.open)
    )
      return this.inputMark;
    const mark = `mark-${++this.counter}`;
    const labels: Record<string, string> = {
      translating: "Move selection",
      resizing: "Resize selection",
      rotating: "Rotate selection",
      draw: "Draw",
      "dragging handle": "Move handle",
    };
    const step: Step = {
      mark,
      label: (name && labels[name]) || name || "Edit contents",
      diff: null,
      selection: [...this.selection()],
      open: true,
      held: options.hold,
      checkpoint: options.checkpoint,
      redoBefore: options.checkpoint ? this.redos : undefined,
    };
    this.undos.push(step);
    // A discrete command closes at the end of this event. Gestures and text
    // sessions hold their action explicitly until completion or cancellation.
    if (!options.hold)
      queueMicrotask(() => {
        if (!this.disposed) this.commit(mark);
      });
    this.touch();
    return mark;
  }

  commit(id?: string) {
    if (!id || id === this.inputMark) this.inputMark = undefined;
    for (const step of this.undos) {
      if (id && step.mark !== id) continue;
      step.open = false;
      // An outer session checkpoint must survive committed child gestures.
      if (!id && step.checkpoint) continue;
      step.checkpoint = false;
      step.redoBefore = undefined;
    }
    // Bound retained document snapshots, without expiring live checkpoints.
    while (
      this.undos.length > 500 &&
      !this.undos[0].open &&
      !this.undos[0].checkpoint
    )
      this.undos.shift();
    this.touch();
  }

  notice(message: string) {
    this.message = message;
    this.touch();
  }
  beginInput(label: string) {
    if (this.inputMark) this.commit(this.inputMark);
    return (this.inputMark = this.mark(label, { hold: true }));
  }
  isInProgress() {
    return this.undos.some((step) => step.open && step.held && step.hadChanges);
  }

  private replaying = 0;
  isReplaying() {
    return this.replaying > 0;
  }
  private apply(diff: RecordsDiff<TLRecord>) {
    this.paused++;
    this.replaying++;
    try {
      return documentOnly(
        this.store,
        this.store.extractingChanges(() => {
          this.store.applyDiff(
            applicableHistoryDiff(this.store, diff, this.canChange),
            {
              runCallbacks: true,
            },
          );
        }),
      );
    } finally {
      this.paused--;
      this.replaying--;
    }
  }

  undo() {
    return this.replay(false);
  }
  redo() {
    return this.replay(true);
  }

  private replay(redo: boolean) {
    this.commit();
    const from = redo ? this.redos : this.undos;
    const to = redo ? this.undos : this.redos;
    while (from.length) {
      const step = from.pop()!;
      if (!step.hadChanges) continue;
      const intended =
        step.diff && (redo ? step.diff : reverseRecordsDiff(step.diff));
      if (
        intended &&
        [
          ...Object.values(intended.added).map(
            (after) => [undefined, after] as const,
          ),
          ...Object.values(intended.updated).map(
            ([, after]) => [this.store.get(after.id), after] as const,
          ),
          ...Object.values(intended.removed).map(
            (before) => [this.store.get(before.id), undefined] as const,
          ),
        ].some(
          ([before, after]) =>
            !this.canChange(
              before as TLRecord | undefined,
              after as TLRecord | undefined,
            ),
        )
      ) {
        from.push(step);
        this.notice(
          `Could not ${redo ? "redo" : "undo"} ${step.label ?? "this action"}: its contents are locked or read-only.`,
        );
        return false;
      }
      const applied = intended && this.apply(intended);
      // One press targets one action. Never silently jump past a conflicted
      // action and change an older, unrelated part of the board.
      if (!applied) {
        this.notice(
          `Could not ${redo ? "redo" : "undo"} ${step.label ?? "this action"}: its contents changed or are locked.`,
        );
        return false;
      }
      step.diff = redo ? applied : reverseRecordsDiff(applied);
      to.push(step);
      this.restoreSelection(step.selection);
      this.message = "";
      this.touch();
      return true;
    }
    this.touch();
    return false;
  }

  /** Revert everything since the mark `id` and forget it (no redo). */
  bailToMark(id: string) {
    let index = -1;
    for (let i = this.undos.length - 1; i >= 0 && index < 0; i--)
      if (this.undos[i].mark === id) index = i;
    if (index < 0) return;
    const cancelled = this.undos.splice(index);
    const branch = cancelled.find((step) => step.redoBefore)?.redoBefore;
    for (const step of [...cancelled].reverse())
      if (step.diff) this.apply(reverseRecordsDiff(step.diff));
    if (branch) this.redos = branch;
    this.touch();
  }

  canUndo() {
    this.version.get();
    return this.undos.some((s) => s.hadChanges);
  }
  canRedo() {
    this.version.get();
    return this.redos.some((s) => s.hadChanges);
  }
  clear() {
    this.inputMark = undefined;
    this.undos = [];
    this.redos = [];
    this.message = "";
    this.touch();
  }
}
